import styled, { keyframes } from 'styled-components';
import { tokens } from '../design/tokens';

export const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const AdminContainer = styled.div`
  min-height: 100vh;
  background: ${tokens.colors.adminBg};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative;
`;

export const AdminWrapper = styled.div`
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
`;

export const TopHeader = styled.header`
  background: ${tokens.colors.adminSurface};
  border-bottom: 1px solid ${tokens.colors.adminBorder};
  padding: 0 ${tokens.spacing.lg};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  @media (max-width: 768px) {
    padding: 0 ${tokens.spacing.md};
    height: 60px;
  }
`;

export const Logo = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${tokens.colors.adminAccent};
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.sm};

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const SearchBar = styled.div`
  flex: 1;
  max-width: 400px;
  margin: 0 ${tokens.spacing.lg};
  position: relative;

  @media (max-width: 768px) {
    margin: 0 ${tokens.spacing.sm};
    max-width: none;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: ${tokens.spacing.sm} ${tokens.spacing.md} ${tokens.spacing.sm} 40px;
  border: 1px solid ${tokens.colors.adminBorder};
  border-radius: 20px;
  font-size: 14px;
  background: ${tokens.colors.adminSurface};
  color: ${tokens.colors.adminText};
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${tokens.colors.adminAccent};
  }

  &::placeholder {
    color: ${tokens.colors.adminTextMuted};
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${tokens.colors.adminTextMuted};
  font-size: 16px;
`;

export const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
`;

export const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  background: ${tokens.colors.adminAccent};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
`;

export const SideMenu = styled.nav<{ $open?: boolean }>`
  position: fixed;
  top: 70px;
  left: 0;
  width: 250px;
  height: calc(100vh - 70px);
  background: ${tokens.colors.adminSurface};
  border-right: 1px solid ${tokens.colors.adminBorder};
  padding: ${tokens.spacing.xl} 0;
  z-index: 90;
  overflow-y: auto;

  @media (max-width: 768px) {
    top: 60px;
    height: calc(100vh - 60px);
    transform: translateX(${props => props.$open ? '0' : '-100%'});
    transition: transform 0.3s ease;
    box-shadow: 2px 0 8px rgba(0,0,0,0.1);
  }
`;

export const MenuItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  padding: ${tokens.spacing.md} ${tokens.spacing.xl};
  border: none;
  background: ${props => props.$active ? `${tokens.colors.adminAccent}15` : 'transparent'};
  color: ${props => props.$active ? tokens.colors.adminAccent : tokens.colors.adminText};
  text-align: left;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
  transition: all 0.2s;

  &:hover {
    background: ${tokens.colors.adminAccent}10;
    color: ${tokens.colors.adminAccent};
  }

  @media (max-width: 768px) {
    padding: ${tokens.spacing.md} ${tokens.spacing.lg};
    font-size: 15px;
  }
`;

export const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${tokens.colors.adminText};
  font-size: 20px;
  cursor: pointer;
  padding: ${tokens.spacing.sm};
  margin-right: ${tokens.spacing.md};

  @media (max-width: 768px) {
    display: block;
  }
`;

export const MenuOverlay = styled.div<{ $open?: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: ${props => props.$open ? 'block' : 'none'};
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 80;
  }
`;

export const MainContent = styled.main`
  margin-left: 250px;
  margin-top: 70px;
  padding: ${tokens.spacing.lg};
  min-height: calc(100vh - 270px);
  margin-bottom: 200px;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 60px;
    padding: ${tokens.spacing.sm};
    min-height: calc(100vh - 260px);
    margin-bottom: 200px;
  }
`;

export const Section = styled.section`
  background: ${tokens.colors.adminSurface};
  border-radius: 12px;
  padding: ${tokens.spacing.lg};
  margin-bottom: ${tokens.spacing.lg};
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${tokens.spacing.lg};
  padding-bottom: ${tokens.spacing.sm};
  border-bottom: 1px solid ${tokens.colors.adminBorder};
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${tokens.colors.adminText};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
`;

export const AddButton = styled.button`
  background: ${tokens.colors.adminAccent};
  color: white;
  border: none;
  padding: ${tokens.spacing.md} ${tokens.spacing.xl};
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.sm};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #7a04b8;
  }
`;

export const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background: ${tokens.colors.adminSurface};
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0,0,0,0.2);
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${tokens.spacing.xxxl};
  color: ${tokens.colors.adminTextMuted};
`;

export const SpinningLoader = styled.div`
  animation: ${spin} 1s linear infinite;
  display: inline-flex;
  align-items: center;
  margin-right: ${tokens.spacing.sm};
`;

export const ErrorState = styled.div`
  background: #fef2f2;
  color: #dc2626;
  padding: ${tokens.spacing.md};
  border-radius: 8px;
  margin-bottom: ${tokens.spacing.xl};
  border: 1px solid #fecaca;
`;
